import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, Tag, Mail, LogIn, ChevronRight, PlayCircle, Users, CalendarDays } from "lucide-react";
import { useDemoModal } from "@/context/DemoModalContext";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetHeader,
  SheetDescription,
} from "@/components/ui/sheet";

const staticNavItems = [
  {
    href: "/#pricing",
    label: "Precios",
    hint: "Planes y tarifas",
    icon: Tag,
    testId: "header-link-pricing-mobile",
  },
  {
    to: "/quienes-somos",
    label: "Quiénes somos",
    hint: "La historia detrás de EkklesiaOnline",
    icon: Users,
    testId: "header-link-quienes-somos-mobile",
  },
  {
    to: "/contacto",
    label: "Contacto",
    hint: "Escríbenos cuando quieras",
    icon: Mail,
    testId: "header-link-contacto-mobile",
  },
  {
    href: "https://app.ekklesiaonline.app/login",
    label: "Iniciar sesión",
    hint: "Accede a tu cuenta",
    icon: LogIn,
    testId: "header-link-login-mobile",
  },
  {
    cal: "ekklesiaonline-demo",
    label: "Agenda una llamada",
    hint: "Reserva una llamada de 45 min",
    icon: CalendarDays,
    testId: "header-link-agenda-mobile",
  },
];

/** Misma base que HeroSection (gradiente + halos suaves). */
const heroPanelStyle = {
  background: "linear-gradient(168deg, #EEEBFF 0%, #F5F3FF 28%, #FAFAFE 58%, #ffffff 100%)",
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const { openFreeModal } = useDemoModal();

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const next = window.scrollY > 24;
        setScrolled((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const size = "text-[15px]";

  const textLinkClass = `shrink-0 ${size} font-semibold text-ink-2 transition-colors duration-200 hover:text-violet-fuerte`;

  const navShell = scrolled
    ? "bg-white/80 shadow-[0_4px_32px_-8px_rgba(74,58,138,0.12),0_0_0_1px_rgba(238,235,255,0.6)] backdrop-blur-2xl"
    : "bg-white/50 shadow-[0_2px_16px_-4px_rgba(74,58,138,0.06),0_0_0_1px_rgba(238,235,255,0.4)] backdrop-blur-xl";

  const navRowClass =
    "group flex w-full items-center gap-3 rounded-xl border border-violet-light bg-white px-4 py-3.5 shadow-[0_2px_12px_-4px_rgba(74,58,138,0.08)] transition-all duration-200 hover:border-violet-fuerte/35 hover:shadow-[0_6px_24px_-8px_rgba(74,58,138,0.14)] active:scale-[0.99]";

  return (
    <header
      data-testid="header"
      className="fixed left-0 right-0 top-0 z-50 flex justify-center px-3 pt-3 sm:px-4 sm:pt-4"
    >
      <nav
        className={`relative flex h-[52px] w-full max-w-5xl items-center justify-between gap-2 rounded-2xl px-3 transition-all duration-500 sm:h-[56px] sm:gap-3 sm:px-4 md:gap-4 md:px-6 lg:gap-4 ${navShell}`}
      >
        <Link
          to="/"
          data-testid="header-logo"
          className="flex min-w-0 shrink-0 items-center gap-2"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-fuerte">
            <span className={`font-display ${size} font-bold leading-none text-white`}>E</span>
          </div>
          <span className={`font-display ${size} font-bold tracking-tight text-ink`}>Ekklesia</span>
        </Link>

        <div className="hidden min-w-0 flex-1 items-center justify-end gap-2 sm:gap-4 md:gap-6 lg:flex">
          <a href="/#pricing" data-testid="header-link-pricing" className={textLinkClass}>
            Precios
          </a>
          <Link to="/quienes-somos" data-testid="header-link-quienes-somos" className={textLinkClass}>
            Quiénes somos
          </Link>
          <Link to="/contacto" data-testid="header-link-contacto" className={textLinkClass}>
            Contacto
          </Link>
          <a href="https://app.ekklesiaonline.app/login" data-testid="header-link-login" className={textLinkClass}>
            Iniciar sesión
          </a>

          <button
            type="button"
            data-cal-link="ekklesiaonline-demo"
            data-cal-config='{"layout":"month_view"}'
            data-testid="header-cta-agenda"
            className={`inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl border border-violet-light bg-white px-4 font-semibold text-ink-2 transition-colors duration-200 hover:border-violet-fuerte/40 hover:text-violet-fuerte sm:px-5 ${size}`}
          >
            <CalendarDays className="h-4 w-4 shrink-0" />
            Agenda una llamada
          </button>

          <button
            type="button"
            onClick={openFreeModal}
            data-testid="header-cta-button"
            className={`inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl bg-violet-fuerte px-4 font-semibold text-white shadow-[0_2px_8px_-2px_rgba(74,58,138,0.3)] transition-[background-color,box-shadow] duration-200 hover:bg-violet-oscuro sm:px-5 ${size}`}
          >
            <PlayCircle className="h-4 w-4 shrink-0" />
            Empezar gratis
          </button>
        </div>

        <div className="flex items-center lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-violet-light/90 bg-white/90 text-ink shadow-[0_2px_12px_-4px_rgba(74,58,138,0.12)] transition-all duration-200 hover:border-violet-fuerte/25 hover:bg-white hover:shadow-[0_4px_16px_-6px_rgba(74,58,138,0.18)] active:scale-[0.97]"
                aria-label="Abrir menú de navegación"
                data-testid="header-menu-button"
              >
                <Menu className="h-[20px] w-[20px]" strokeWidth={2.25} aria-hidden />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              overlayClassName="z-[90] bg-[#1A1135]/25 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              closeClassName="right-4 top-4 z-[110] flex h-10 w-10 items-center justify-center rounded-xl border border-violet-light bg-white text-ink shadow-[0_2px_12px_-4px_rgba(74,58,138,0.12)] opacity-100 ring-0 transition-all hover:bg-[#FAFAFE] hover:text-violet-fuerte focus:outline-none focus:ring-2 focus:ring-violet-fuerte/25"
              className="z-[100] flex h-full w-[min(100vw,20rem)] max-w-[20rem] flex-col gap-0 overflow-hidden !border-0 !bg-transparent !p-0 shadow-[0_8px_40px_-12px_rgba(74,58,138,0.2)] sm:max-w-[20rem]"
            >
              <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                <div
                  aria-hidden
                  className="pointer-events-none absolute top-20 left-[8%] h-[280px] w-[280px] rounded-full opacity-25 blur-3xl sm:h-[320px] sm:w-[320px]"
                  style={{ background: "radial-gradient(circle, rgba(74,58,138,0.15) 0%, transparent 70%)" }}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute bottom-16 right-[15%] h-[220px] w-[220px] rounded-full opacity-20 blur-3xl"
                  style={{ background: "radial-gradient(circle, rgba(201,162,39,0.12) 0%, transparent 65%)" }}
                />

                <div className="relative flex min-h-0 flex-1 flex-col" style={heroPanelStyle}>
                  <SheetHeader className="space-y-4 px-5 pb-2 pt-14 text-left sm:px-6 sm:pt-16">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">Navegación</p>
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-fuerte shadow-[0_4px_14px_-4px_rgba(74,58,138,0.35)]">
                        <span className={`font-display ${size} font-bold leading-none text-white`}>E</span>
                      </div>
                      <div>
                        <SheetTitle className="font-display text-left text-[15px] font-bold tracking-tight text-ink">
                          Ekklesia
                        </SheetTitle>
                        <SheetDescription className="text-left text-sm text-ink-2">
                          Gestión para iglesias
                        </SheetDescription>
                      </div>
                    </div>
                  </SheetHeader>

                  <nav
                    className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain px-5 pb-4 pt-1 sm:px-6"
                    aria-label="Navegación móvil"
                  >
                    {staticNavItems.map((item) => {
                      const Icon = item.icon;
                      const inner = (
                        <>
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FAFAFE] text-violet-fuerte ring-1 ring-violet-light/80">
                            <Icon className="h-[17px] w-[17px]" strokeWidth={2.2} aria-hidden />
                          </div>
                          <div className="min-w-0 flex-1 text-left">
                            <span className="font-display block text-[15px] font-semibold text-ink">
                              {item.label}
                            </span>
                            <span className="mt-0.5 block text-[13px] leading-snug text-ink-3">
                              {item.hint}
                            </span>
                          </div>
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-ink-4 opacity-70 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-violet-fuerte group-hover:opacity-100"
                            aria-hidden
                          />
                        </>
                      );
                      return (
                        <SheetClose key={item.testId} asChild>
                          {item.cal ? (
                            <button
                              type="button"
                              data-cal-link={item.cal}
                              data-cal-config='{"layout":"month_view"}'
                              data-testid={item.testId}
                              className={navRowClass}
                            >
                              {inner}
                            </button>
                          ) : item.to ? (
                            <Link to={item.to} data-testid={item.testId} className={navRowClass}>
                              {inner}
                            </Link>
                          ) : (
                            <a
                              href={item.href}
                              target={item.external ? "_blank" : undefined}
                              rel={item.external ? "noopener noreferrer" : undefined}
                              data-testid={item.testId}
                              className={navRowClass}
                            >
                              {inner}
                            </a>
                          )}
                        </SheetClose>
                      );
                    })}
                  </nav>

                  <div className="shrink-0 border-t border-violet-light/70 bg-white/50 px-5 py-5 sm:px-6">
                    <SheetClose asChild>
                      <button
                        type="button"
                        onClick={openFreeModal}
                        data-testid="header-cta-button-mobile"
                        className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte px-6 text-[17px] font-semibold text-white shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)] transition-all duration-300 hover:bg-violet-oscuro hover:shadow-[0_10px_32px_-6px_rgba(74,58,138,0.5)] active:scale-[0.99]"
                      >
                        <PlayCircle className="h-5 w-5" />
                        Empezar gratis
                      </button>
                    </SheetClose>
                    <p className="mt-3 text-center text-[13px] text-ink-3">
                      Sin tarjeta · Sin compromiso · Acceso en minutos
                    </p>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
